import {Component, EventEmitter, Input, OnDestroy, Output, ViewChild} from '@angular/core';
import {Const, PolicyAction, ResourceState, ResourceType} from '../../../../../../core/helpers/Const';
import { Utils } from '../../../../../../core/helpers/Utils';
import { ResourceModel } from '../../../../../../core/models/resources/resource.model';
import { NotificationsService } from '../../../../../../service/services/misc/notifications/notifications.service';
import { ThemeSubjectModel } from '../../../../../../service/services/misc/theme/theme-subject.model';
import {firstValueFrom, Subscription} from 'rxjs';
import { ThemeService } from '../../../../../../service/services/misc/theme/theme.service';
import {UisService} from "../../../../../../service/services/misc/uis/uis.service";
import {SwaService} from "../../../../../../service/services/misc/swa/swa.service";
import {JsonEditorComponent} from "../../../../../../shared/components/json-editor/json-editor.component";
import {ResourceDataService} from "../../../../../../service/services/rest/resource-data/resource-data.service";
import {
    MoveResourceStatueDialogComponent
} from "../../../../dialogs/move-resource-status-dialog/move-resource-status-dialog.component";
import {DialogService} from "../../../../../../service/services/misc/dialog/dialog.service";

@Component({
    selector: 'app-details',
    templateUrl: './details.component.html',
    styleUrls: ['./details.component.scss'],
    standalone: false
})
export class DetailsComponent implements OnDestroy {
    Object = Object;
    Utils = Utils;
    Const = Const;

    @ViewChild(JsonEditorComponent)
        editor : JsonEditorComponent;
    @Input() loading = false;

    details: any;
    resource: ResourceModel;
    detailsSelected = 'form';
    provisioningMethod: string;
    status: ResourceState;
    labels: { [key: string]: string };
    annotations: { [key: string]: string };
    indices: any[] = [];
    yamlText: string;
    theme: any;
    themeSubscription: Subscription;
    showCopyPath = true;
    showFullScreen = true;
    isCollapsed = false;
    isExpanded = false;
    isYamlExpanded = false;
    yamlEditablePermissions = false;
    @Output() reloadResource: EventEmitter<ResourceModel> = new EventEmitter<ResourceModel>();
    @Output() onEditResource: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() onReconcileResource: EventEmitter<boolean> = new EventEmitter<boolean>();

    constructor(
        private notificationsService: NotificationsService,
        private themeService: ThemeService,
        private uisService: UisService,
        private swaService: SwaService,
        private _resourceDataService: ResourceDataService,
        private dialogService: DialogService
    ) {}

    ngOnDestroy() {
        if (this.themeSubscription) {
            this.themeSubscription.unsubscribe();
        }
    }

    initDetails(resource: ResourceModel) {
        const originalResourceUid = this.resource?.uid;
        const newResourceUid = resource?.uid;
        this.resource = resource;
        this.details = this.resource?.resource;
        this.labels = this.resource.labels;
        this.annotations = this.resource.annotations;
        this.indices = this.details?.spec?.indices;
        const fields = ["apiVersion", "type", "metadata", "spec", "status"];
        const sortedJson = Utils.rearrangeJsonOrder(this.details, fields);
        this.yamlText = Utils.convertToYamlFile(sortedJson);
        this.isCollapsed = false;
        this.isExpanded = false;

        if (this.editor && originalResourceUid !== newResourceUid) {
            setTimeout(() => {
                this.initEditor(this.editor.editor);
            }, 0);
        }
        if (this.resource.state) {
            this.status = this.resource.state;
        }
        if (this.details?.type === ResourceType.MANAGED_SERVICE) {
            this.provisioningMethod = this.getProvisioningMethod();
        }

        this.themeSubscription = this.themeService.themeSubject.asObservable().subscribe((theme: ThemeSubjectModel) => {
            this.theme = Const.YAML_THEMES[theme.selectedTheme];
        });
    }

    initEditor(editor: any) {
        Utils.initEditor(editor, "metadata:...", "spec:...");
        this.yamlEditablePermissions = false;
    }

    getProvisioningMethod(): string {
        const prov = this.details?.spec?.provisioning;
        if (prov && Object.keys(this.details?.spec.provisioning).length > 0) {
            return Const.PROVISIONING_METHODS[Object.keys(prov)[0]];
        } else {
            return Const.PROVISIONING_METHODS['external'];
        }
    }

    onCollapseYaml() {
        Utils.setEditorDefaultState("metadata:...", "spec:...", this.editor.editor.getSession(), true);
        this.isCollapsed = true;
        this.isExpanded = false;
    }

    onExpandYaml() {
        this.editor.setExpand();
        this.isCollapsed = false;
        this.isExpanded = true;
    }


    copyYaml() {
        this.uisService.sendEvent(this.uisService.UIS_EVENT_TYPES.COPY_YAML);
        this.swaService.sendEvent(this.swaService.SWA_EVENT_TYPES.COPY_YAML);
        let yaml = Utils.convertToYamlFile(this.details);
        this.notificationsService.showMessageToast('YAML copied to clipboard');
        Utils.copyToClipboard(yaml);
    }

    onCopyPath() {
        this.notificationsService.showMessageToast($localize`:XMSG|Toast for copying path@@general.copyPathToast:Path copied to your clipboard`);
        Utils.copyToClipboard(this.resource.path);
    }

    selectYaml() {

        this.swaService.sendCustomEvent(this.swaService.SWA_EVENT_TYPES.SELECT_YAML_TAB, [this.details?.type], []);
    }

    isEditable(resource: ResourceModel) {
        if (this.yamlEditablePermissions) {
            return true;
        }
        const isValidType =
            resource.type !== ResourceType.FOLDER &&
            resource.type !== ResourceType.RESOURCE_GROUP &&
            resource.type !== ResourceType.ORGANIZATION;

        let rules = resource.rules || [];

        this.yamlEditablePermissions = isValidType && rules.some(r => r.action === PolicyAction.Update || r.action === PolicyAction.All);
        return this.yamlEditablePermissions;
    }

    async openOperationDetails() {
        try {
            const resourceStatus = await firstValueFrom(this._resourceDataService.getMoveStatus(this.resource.resource.metadata.mover.operationID));
            const data = {
                operationId: this.resource.resource.metadata?.mover?.operationID,
                status: resourceStatus.status
            };
            this.dialogService.openDialog(MoveResourceStatueDialogComponent, data);
        } catch (e) {
            console.log("Could not get move resource status", e);
        }
    }
}
